/* eslint-disable @typescript-eslint/naming-convention */
import { Directive, EventEmitter, Injector, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, EMPTY, of, Subject, Subscription, throwError } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import { IUser } from '@portal/entity-services/interfaces';
import { UserService as EsUserService } from '@portal/entity-services/users';
import { AuthenticationService } from '@portal/shared/auth/authentication';
import { AuthorizationService } from '@portal/shared/auth/authorization';
import { Features } from 'environments/enums/features.enum';
import { NotifierService } from '@portal/shared/ui/notifier';
import { UserService } from '@portal/shared/user';
import { AppPermission } from '@portal/shared/auth/authorization/src/lib/interfaces/app-permission.interface';
import { FeatureToggle } from '@portal/shared/auth/feature-toggle/src';
import { EnvironmentVariableService } from '@portal/shared/helpers';
import { WhiteLabelingService } from '@portal/shared/white-labeling/src';
import { UserActivityService } from '@portal/shared/user/src/lib/user-activity.service';
import { Location } from '@angular/common';

const NO_AUTO_LOGIN_REDIRECT_QUERY_KEY = 'no-auto-login-redirect';

// One-shot marker so the SSO state-injection reload happens at most once per
// authorization code, preventing an infinite reload loop if verifyState fails.
const SSO_STATE_INJECTED_KEY = 'sso_state_injected';
// Client id the CommonPortal PKCE code/state were issued for. The bare Asgardeo
// SSO redirect does not echo it, so it must be re-added before the code exchange.
const COMMON_PORTAL_CLIENT_ID = 'CommonPortal';

@UntilDestroy()
@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class PortalBase implements OnInit, OnDestroy {
  @Output() whiteLabelLoaded = new EventEmitter<void>();

  router: Router;
  route: ActivatedRoute;

  showLoader = true;
  isAuthenticated = false;
  isWhiteLabelingEnabled: boolean;

  authenticatedUser: IUser;
  appsPermissions: AppPermission[];

  userService: UserService;
  cba = !!EnvironmentVariableService.getVar('CBA');

  showUnauthenticatedOutlet: boolean = false; // Default to false
  showHeader = true;
  isWebView: boolean;
  appType: string;

  protected esUserService: EsUserService;
  protected notifierService: NotifierService;
  protected featureToggleService: FeatureToggle;
  protected authorizationService: AuthorizationService;
  protected authenticationService: AuthenticationService;
  protected whiteLabelingService: WhiteLabelingService;
  protected location: Location;
  protected userActivityService: UserActivityService;

  protected authLoadingSubject = new Subject<boolean>();
  protected whiteLabelLoadingSubject = new Subject<boolean>();
  protected authLoading$ = this.authLoadingSubject.asObservable();
  protected whiteLabelLoading$ = this.whiteLabelLoadingSubject.asObservable();

  protected subscription: Subscription;
  private getAuthorizationSub: Subscription;

  constructor(injector: Injector) {
    this.userService = injector.get(UserService);
    this.esUserService = injector.get(EsUserService);
    this.router = injector.get(Router);
    this.route = injector.get(ActivatedRoute);
    this.authenticationService = injector.get(AuthenticationService);
    this.authorizationService = injector.get(AuthorizationService);
    this.featureToggleService = injector.get(FeatureToggle);
    this.notifierService = injector.get(NotifierService);
    this.whiteLabelingService = injector.get(WhiteLabelingService);
    this.location = injector.get(Location);
    this.userActivityService = injector.get(UserActivityService);
  }

  ngOnInit(): void {
    this.subscribeToLoading();

    this.initWhiteLabel();

    this.subscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        withLatestFrom(this.route.queryParams),
        mergeMap((data: any) => {
          const [, params] = data;
          const { code, state, error,client_id, session_state } = params;
          // IdP-initiated login: the callback carries `state=idp-init-<fidp>`, and the code
          // is not bound to a PKCE verifier of this browser. Discard it and start our own
          // authorize request for the same fidp; the response (code + session_state, no
          // state) is then handled by the regular exchange below. Must run first, as a
          // verifier from an earlier login may still be in localStorage.
          const idpInitiatedFidp = this.authenticationService.getIdpInitiatedFidp(state);
          if (code && session_state && idpInitiatedFidp) {
            this.authenticationService.authenticateIdpInitiated(idpInitiatedFidp);
            return EMPTY;
          }
          // WSO2 / Asgardeo SSO returns `session_state` (an OIDC session-management
          // value, NOT the CSRF `state`) on the callback, and the authorize request
          // carries no `state`. Presence of `session_state` + `code` marks the SSO
          // login callback; the PKCE code_verifier provides the binding, so exchange
          // the code for a token (userAccessToken), defaulting the client_id to
          // CommonPortal since it is not echoed back.
          if (code && session_state && localStorage.getItem('pkce_code_verifier')) {
            localStorage.removeItem(SSO_STATE_INJECTED_KEY);
            return this.authenticationService.exchangeCodeForToken(
              client_id ?? COMMON_PORTAL_CLIENT_ID,
              code,
            );
          }
          // SSO (Azure via Asgardeo) redirects back with only ?code (no state /
          // client_id). Re-inject the CommonPortal state (persisted in
          // localStorage during authenticate()) + client_id, then reload so the
          // standard code+state exchange below can run. Guarded to run once per
          // code to avoid a reload loop if the injected state fails verifyState.
          if (!session_state && this.shouldInjectSsoState(code, state)) {
            this.injectSsoStateAndReload(code, params);
            return EMPTY;
          }
          if (code && state && this.authenticationService.verifyState(state)) {
            localStorage.removeItem(SSO_STATE_INJECTED_KEY);
            return this.authenticationService.exchangeCodeForToken(client_id, code);
          }
          if (error) {
            return of({ error });
          }
          return of(params);
        }),
        catchError((error) => {
          const redirectPath = localStorage.getItem('redirect_path');
          this.authenticationService.logout({ skipLocationChange: true, redirectPath });
          return throwError(error);
        }),
      )
      .subscribe((result: any) => {
        this.showUnauthenticatedOutlet = this.getRouteData('showUnauthenticatedOutlet', false);
        this.isWebView = this.route.snapshot.queryParams.hasOwnProperty('embedded');
        this.appType = this.route.snapshot.queryParams.appType;
        this.showHeader = this.getRouteData('showHeader', true);
        this.authenticationService.isSessionStarted = !!localStorage.getItem('session_start_time');

        this.setAuthLoadingState(true);
        result = this.getParameters(result);
        const { access_token, id_token, scope, token_type, expires_in, error } = result;
        const isAuthCallback = access_token && scope && id_token && token_type && expires_in;
        const redirectPath = isAuthCallback
          ? localStorage.getItem('redirect_path') ?? ''
          : this.location.path(true);
        if (error) {
          this.authenticationService.logout({ skipLocationChange: true, redirectPath });
          return;
        }
        if (isAuthCallback) {
          this.authenticationService.handleAuthResponse(result);
          if (
            this.authorizationService.isPhoneNumberVerified === undefined ||
            this.authorizationService.isPhoneNumberVerified === null
          ) {
            this.authorizationService.isPhoneNumberVerified =
              this.authorizationService.getPhoneNumberVerificationStatus();
          }
        } else if (
          !this.userService.isLogin() &&
          !this.showUnauthenticatedOutlet &&
          !(this.hasNoAutoLoginRedirectQueryParam() || this.cba || this.hasIonicFlags())
        ) {
          this.signIn();
          return;
        }

        if (!this.getAuthorizationSub && this.authenticationService.userUid) {
          let enableUserEditRedirect =
            !this.authenticationService.isSessionStarted &&
            !this.authorizationService.isPhoneNumberVerified &&
            this.authorizationService
              .getIdTokenPermissions()
              .some((permission) => permission.featureId === 'ALERT_ADD_MOBILE_NO') &&
            this.featureToggleService.isConfigFeatureActive(Features.phoneNumberVerification);
          this.getAuthorizationSub = this.authorizationService.authorize().subscribe(
            async ({ user, appsPermissions }) => {
              this.authenticatedUser = user;
              this.userService.userDetails = user;
              this.appsPermissions = appsPermissions;
              enableUserEditRedirect =
                enableUserEditRedirect && user.roles.some((role) => role === 'PETRO_SITE_ADMIN');

              if (isAuthCallback) {
                let path =
                  redirectPath ||
                  (this.location.path(true)?.charAt(0) !== '?' && this.location.path(true)) ||
                  '/';

                const currentQueryParams = { ...this.route.snapshot.queryParams };

                // Strip the OAuth/SSO callback params from the post-login redirect
                // URL. An SSO login callback carries `session_state` (+ `code`); if
                // these are carried into a deep-link redirect (e.g.
                // /payments/reports/transactions/view/orders) the NavigationEnd
                // subscription above re-reads them and re-runs exchangeCodeForToken()
                // with an already-consumed code, which fails and logs the user out.
                // Only strip when the SSO callback marker (`session_state`) is
                // present, so the regular flow's legitimate query params are kept.
                if (currentQueryParams['session_state']) {
                  delete currentQueryParams['session_state'];
                  delete currentQueryParams['code'];
                  delete currentQueryParams['state'];
                }

                // Check if path already contains a query string
                const hasQuery = path.includes('?');
                let queryString = new URLSearchParams(currentQueryParams).toString();

                if (queryString.includes('iss') && queryString.includes('client_id')) {
                  queryString = '';
                }
                let fullPath = !hasQuery && queryString ? `${path}?${queryString}` : path;

                // Base href (MFE root, e.g. "/payments") of the app login was
                // started from. When the OAuth callback returns to the root/
                // wrapper app instead of that MFE, the deep-link route lives in a
                // different compiled app and the current router cannot reach it.
                const storedBaseHref = localStorage.getItem('redirect_base_href') || '';
                const currentPath = window.location.pathname;
                const isInTargetApp =
                  !storedBaseHref ||
                  currentPath === storedBaseHref ||
                  currentPath.startsWith(`${storedBaseHref}/`);

                if (enableUserEditRedirect) {
                  const app = this.authorizationService.getApps(appsPermissions)?.[0].path || '';
                  fullPath = `${window.location.origin}/${app}/administration/users/${user.userUid}/edit`;
                  location.assign(fullPath);
                } else if (!isInTargetApp) {
                  // Callback landed outside the target MFE: append the stored base
                  // href and do a full document load so the browser fetches the
                  // correct app from the server (client-side routing can't switch
                  // MFEs). This is the one unavoidable extra redirect.
                  location.assign(`${window.location.origin}${storedBaseHref}${fullPath}`);
                } else {
                  // Callback landed inside the correct app: navigate in-app, no
                  // reload. navigateByUrl re-applies this app's base href once.
                  await this.router.navigateByUrl(fullPath, {
                    replaceUrl: true,
                  });
                }
                localStorage.removeItem('redirect_path');
                localStorage.removeItem('redirect_base_href');
              }

              this.authenticationService.authEvents.next({
                type: AuthenticationService.Event.LoggedIn,
              });

              if (this.featureToggleService.isFeatureActive(Features.SilentRefreshToken)) {
                this.authenticationService.startTokenRefreshTimers();
              }
            },
            () => {
              this.authenticationService.logout({ skipLocationChange: true, redirectPath });
            },
          );
        }

        if (this.isAuthenticated || !this.getAuthorizationSub) {
          this.setAuthLoadingState(false);
        }
      });
    if (this.userService.isLogin()) {
      this.userService.setCurrentUser();
    }
  }

  private getRouteData(key: string, fallBack: boolean): boolean {
    // Try current route first
    if (this.route.snapshot.data[key] !== undefined) {
      return this.route.snapshot.data[key];
    }

    // Try first child route
    if (this.route.children && this.route.children.length > 0) {
      return this.route.children[0]?.snapshot?.data[key];
    }

    // Try to find any child with the data
    if (this.route.children) {
      for (const child of this.route.children) {
        if (child.snapshot.data[key] !== undefined) {
          return child.snapshot.data[key];
        }
      }
    }

    return fallBack;
  }

  getParameters(result): any {
    result = {
      ...result,
      access_token: JSON.parse(localStorage.getItem('access_token')) || result.access_token,
      id_token: JSON.parse(localStorage.getItem('id_token')) || result.id_token,
      scope: JSON.parse(localStorage.getItem('scope')) || result.scope,
      token_type: JSON.parse(localStorage.getItem('token_type')) || result.token_type,
      expires_in: JSON.parse(localStorage.getItem('expires_in')) || result.expires_in,
    };
    return result;
  }

  /**
   * True only for the SSO landing case: an authorization code is present but no
   * state was echoed back (Asgardeo/Azure omit it), a persisted pkce_state
   * exists to re-attach, and we have not already attempted the injection for
   * this code (one-shot guard against a reload loop).
   */
  private shouldInjectSsoState(code: string, state: string): boolean {
    return (
      !!code &&
      !state &&
      !!localStorage.getItem('pkce_state') &&
      sessionStorage.getItem(SSO_STATE_INJECTED_KEY) !== code
    );
  }

  /**
   * Rebuild the callback URL with the persisted CommonPortal state and client_id
   * and reload, so the standard code+state exchange in ngOnInit can complete.
   * Preserves any existing query params (e.g. iss, session_state) except the
   * ones we are (re)setting.
   */
  private injectSsoStateAndReload(code: string, params: Record<string, string>): void {
    const storedState = localStorage.getItem('pkce_state') ?? '';
    // Mark this specific code as attempted BEFORE reloading so a failed
    // verifyState on the next pass cannot loop back through here.
    sessionStorage.setItem(SSO_STATE_INJECTED_KEY, code);

    const search = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        search.set(key, String(value));
      }
    });
    search.set('code', code);
    search.set('state', storedState);
    search.set('client_id', COMMON_PORTAL_CLIENT_ID);

    window.location.assign(`${window.location.origin}${window.location.pathname}?${search.toString()}`);
  }

  signIn(withoutRedirect: boolean = false, logoutParam = ''): void {
    if (this.isWhiteLabelingEnabled) {
      this.whiteLabelingService
        .loadParameters()
        .pipe(untilDestroyed(this))
        .subscribe((parameters) => {
          const loginDomainName = parameters?.text?.loginDomainName;
          if (loginDomainName) {
            this.authenticationService.setLoginDomainName(loginDomainName);
          }

          if (withoutRedirect) {
            this.authenticationService.authenticate(undefined, logoutParam, this.isWebView, this.appType);
          } else {
            this.authenticationService.authenticate(
              this.location.path(true),
              logoutParam,
              this.isWebView,
              this.appType,
            );
          }
        });
    } else if (withoutRedirect) {
      this.authenticationService.authenticate(undefined, logoutParam, this.isWebView, this.appType);
    } else {
      this.authenticationService.authenticate(
        this.location.path(true),
        logoutParam,
        this.isWebView,
        this.appType,
      );
    }
  }

  logout(): void {
    this.authenticationService.logout();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.getAuthorizationSub?.unsubscribe();
    this.authenticationService.removeTokenRefreshTimers();
  }

  protected setAuthLoadingState(state: boolean): void {
    this.authLoadingSubject.next(state);
  }

  protected setWhiteLabelLoadingState(state: boolean): void {
    this.whiteLabelLoadingSubject.next(state);
  }

  private initWhiteLabel(): void {
    this.isWhiteLabelingEnabled = this.featureToggleService.isConfigFeatureActive(
      Features.PortalWhiteLabeling,
    );

    if (this.isWhiteLabelingEnabled) {
      this.loadWhiteLabelingSettings();
    }
  }

  private loadWhiteLabelingSettings(): void {
    this.setWhiteLabelLoadingState(true);
    this.whiteLabelingService
      .isWhiteLabelingExists()
      .pipe(untilDestroyed(this), take(1))
      .subscribe(
        (isExist) => {
          if (isExist) {
            this.applyWhiteLabeling();
          } else {
            this.setWhiteLabelLoadingState(false);
          }
        },
        () => this.setWhiteLabelLoadingState(false),
      );
  }

  private hasNoAutoLoginRedirectQueryParam(): boolean {
    const queryParams = this.route.snapshot.queryParams;
    return queryParams.hasOwnProperty(NO_AUTO_LOGIN_REDIRECT_QUERY_KEY);
  }

  private hasIonicFlags(): boolean {
    return !!EnvironmentVariableService.getVar('IONIC.ON');
  }

  private subscribeToLoading(): void {
    combineLatest([
      this.authLoading$.pipe(startWith(true)),
      this.whiteLabelLoading$.pipe(startWith(false)),
    ])
      .pipe(
        map(([...loadings]) => loadings.some((isLoading) => isLoading)),
        distinctUntilChanged(),
        // Ensure minimum loading time to avoid flash of blank content
        startWith(true),
      )
      .subscribe((isLoading) => (this.showLoader = isLoading));
  }

  private applyWhiteLabeling(): void {
    this.whiteLabelingService
      .loadCollection()
      .pipe(
        untilDestroyed(this),
        take(1),
        finalize(() => this.setWhiteLabelLoadingState(false)),
      )
      .subscribe(([parameters, logo, favicon]) => {
        if (logo) {
          this.whiteLabelLoaded.emit();
        }
        if (favicon) {
          this.whiteLabelingService.applyFavicon();
        }
        if (parameters) {
          const stylesApplied = this.whiteLabelingService.applyStyles();
          const fontApplied = this.whiteLabelingService.applyGoogleFont(
            parameters?.pageStyle?.font?.[0],
          );
          if (stylesApplied || fontApplied) {
            this.whiteLabelingService.applyWhiteLabeling();
          }
        }
      });
  }
}
